import React, {useState, useEffect, useRef} from "react";
import {Field, Form, Formik} from "formik";
import Loader from "../../loaders/Loader";
import MessageSendingData from "../MessageSendingData";
import CustomMultiSelect from "../../CustomMultiSelect";
import {getAnOfferCountries} from "../../../helpers/get-an-offers-countries";
import {AUTH_TOKEN} from "../../../constants/Auth";
import {useDispatch} from "react-redux";
import {logOut} from "../../../redux/dataUploader/dataSlice";

const getCountryFilterValues = (countryFilter) => {
    if (!countryFilter?.value) return [];

    const values = Array.isArray(countryFilter.value) ? countryFilter.value : [countryFilter.value];

    return values
        .flatMap(value => String(value).split(","))
        .map(value => value.trim().replaceAll("%", "").toLowerCase())
        .filter(Boolean);
};

const filterCountriesByOfferCountry = (countries, countryFilter) => {
    if (!countryFilter?.operator) return countries;

    const filteredValues = getCountryFilterValues(countryFilter);

    if (filteredValues.length === 0) return countries;

    if (["like", "in_json", "="].includes(countryFilter.operator)) {
        return countries.filter(country => filteredValues.includes(country.value.toLowerCase()));
    }

    if (["not like", "not_json", "!="].includes(countryFilter.operator)) {
        return countries.filter(country => !filteredValues.includes(country.value.toLowerCase()));
    }

    return countries;
};

const CSVinEmlFormatPopup = ({
                                 closePopup,
                                 anAccount,
                                 userId,
                                 selectedCountryOperator,
                                 selectedCountryFromFilter,
                                 urlSearchParams,
                                 filterParams,
                             }) => {
    const dispatch = useDispatch();

    const [countriesList, setCountriesList] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [emlVersion, setEmlVersion] = useState("v4");
    const [loading, setLoading] = useState(false);

    const [confirmationPopupText, setConfirmationPopupText] = useState("");
    const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);

    const token = localStorage.getItem(AUTH_TOKEN);
    const loggedUserEmail = localStorage.getItem("email");

    const checkSession = (status) => {
        if (status === 401) dispatch(logOut());
    };

    const buildWhereFromFilters = (filters) => {
        const where = { global_join_by: "AND" };

        if (!filters) return where;

        Object.keys(filters).forEach(key => {
            const {operator, value} = filters[key];
            where[key] = {
                operator: operator,
                value: [value],
                join_by: "AND"
            };
        });

        return where;
    };

    useEffect(() => {
        const loadCountries = async () => {
            const anCountries = await getAnOfferCountries();
            let arr = [];
            const activeCountryFilter = filterParams?.offer_country || urlSearchParams.offer_country;
            const filteredCountryValues = getCountryFilterValues(activeCountryFilter);

            for (let country in anCountries) {
                arr.push({ value: country, label: anCountries[country] });
            }

            arr = filterCountriesByOfferCountry(arr, activeCountryFilter);

            setCountriesList(arr);

            if (["like", "in_json", "="].includes(activeCountryFilter?.operator)) {
                setSelectedCountries(filteredCountryValues);
            } else {
                setSelectedCountries([]);
            }
        };

        loadCountries();
    }, [filterParams, urlSearchParams.offer_country, selectedCountryOperator, selectedCountryFromFilter]);

    const handleSubmit = async () => {
        setLoading(true);

        const whereParams = (filterParams && Object.keys(filterParams).length > 0)
            ? buildWhereFromFilters(filterParams)
            : urlSearchParams;

        const body = {
            email: loggedUserEmail,
            countries: selectedCountries.length > 0 ? selectedCountries : [],
            where: JSON.stringify(whereParams),
            eml_version: emlVersion,
        };

        try {
            const res = await fetch(
                `${localStorage.getItem("apiUrl")}api/affiliate-network/download-offers-eml-format/${anAccount}/${userId}`,
                {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(body),
                }
            );

            checkSession(res.status);

            const data = await res.json();
            setConfirmationPopupText(data.message || "Email sent.");
            setShowConfirmationPopup(true);

        } catch (e) {
            setConfirmationPopupText("Something went wrong.");
            setShowConfirmationPopup(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-item-popup-container voluum-popup-container">
            <div className="add-item-popup-block">
                <div className="form-wrap">
                    <div className="title-wrap">
                        <h1>Download CSV - EML Format</h1>
                        <div className="close-btn" onClick={closePopup}>
                            <img
                                src={`${process.env.PUBLIC_URL}/images/close-btn.svg`}
                                alt="close popup"
                            />
                        </div>
                    </div>

                    <div className="form-flex-container">

                        <div className="input-block without-config-item-block input-search-select-block">
                            <label>Choose one or more countries (all countries selected by default)</label>

                            <CustomMultiSelect
                                key={`${countriesList.length}-${selectedCountries.join(",")}`}
                                options={countriesList}
                                defaultValue={countriesList.filter(country => selectedCountries.includes(country.value))}
                                onChange={(vals) =>
                                    setSelectedCountries(vals)
                                }
                            />
                        </div>

                        <div>
                            <form>
                                <div className="input-block config-item-block checkbox-block">
                                    <input
                                        type="checkbox"
                                        className="toggle-button"
                                        id="eml-v2"
                                        checked={emlVersion === "v2"}
                                        onChange={() => setEmlVersion("v2")}
                                    />
                                    <label htmlFor="eml-v2" className="toggle-label">
                                        EML v2 – Legacy (No Offers Status)
                                    </label>
                                </div>

                                <div className="input-block config-item-block checkbox-block">
                                    <input
                                        type="checkbox"
                                        className="toggle-button"
                                        id="eml-v3"
                                        checked={emlVersion === "v3"}
                                        onChange={() => setEmlVersion("v3")}
                                    />
                                    <label htmlFor="eml-v3" className="toggle-label">
                                        EML v3 – Offer Status Supported
                                    </label>
                                </div>

                                <div className="input-block config-item-block checkbox-block">
                                    <input
                                        type="checkbox"
                                        className="toggle-button"
                                        id="eml-v4"
                                        checked={emlVersion === "v4"}
                                        onChange={() => setEmlVersion("v4")}
                                    />
                                    <label htmlFor="eml-v4" className="toggle-label">
                                        EML v4 – Notes Column Included
                                    </label>
                                </div>

                                <div className="hint-custom after-checkbox-block">
                                    Tip: v3 includes Optim8 Offer ID and offer status fields. v4 adds a Notes column (empty by default — filled by JV directly in EML).
                                </div>
                            </form>
                        </div>

                        <div className="input-block without-config-item-block after-checkbox-block">
                            {loading ? (
                                <Loader />
                            ) : (
                                <button
                                    type="button"
                                    className="blue-btn"
                                    onClick={handleSubmit}
                                >
                                    Submit
                                </button>
                            )}
                        </div>

                    </div>
                </div>
            </div>

            {showConfirmationPopup && (
                <MessageSendingData
                    messageText={confirmationPopupText}
                    setClosePopup={() => setShowConfirmationPopup(false)}
                />
            )}
        </div>
    );
};

export default CSVinEmlFormatPopup;
